import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Mic2, MessageSquare, Trophy, Calendar, Star, TrendingUp, Play, Pencil, Check, X, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const getTierColor = (tier) => {
    const map = {
        rookie: 'tier-rookie', contender: 'tier-contender', prospect: 'tier-prospect',
        pro: 'tier-pro', elite: 'tier-elite', legend: 'tier-legend',
    };
    return map[tier] || map.rookie;
};

const getScoreColor = (score) => {
    if (score >= 9) return 'text-truth';
    if (score >= 7) return 'text-[#00E5FF]';
    if (score >= 5) return 'text-neon-blue';
    return 'text-zinc-400';
};

const ProfilePage = () => {
    const { userId } = useParams();
    const { user: currentUser } = useAuth();
    
    const [profile, setProfile] = useState(null);
    const [performances, setPerformances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState({ artist_name: '', location: '', bio: '' });
    const [saving, setSaving] = useState(false);

    const isOwnProfile = currentUser?.user_id === userId;

    useEffect(() => {
        fetchProfile();
        fetchPerformances();
    }, [userId]);

    const fetchProfile = async () => {
        try {
            const response = await axios.get(`${API}/users/${userId}`);
            setProfile(response.data);
        } catch (error) {
            console.error('Failed to fetch profile:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchPerformances = async () => {
        try {
            const response = await axios.get(`${API}/users/${userId}/performances`);
            setPerformances(response.data);
        } catch (error) {
            console.error('Failed to fetch performances:', error);
        }
    };

    const startEdit = () => {
        setDraft({
            artist_name: profile.artist_name || '',
            location: profile.location || '',
            bio: profile.bio || '',
        });
        setEditing(true);
    };

    const cancelEdit = () => setEditing(false);

    const saveProfile = async () => {
        setSaving(true);
        try {
            const token = localStorage.getItem('trapworld_token');
            const payload = { location: draft.location.trim(), bio: draft.bio.trim() };
            if (draft.artist_name.trim()) payload.artist_name = draft.artist_name.trim();
            const response = await axios.put(
                `${API}/users/${userId}`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setProfile(prev => ({ ...prev, ...response.data }));
            setEditing(false);
            fetchPerformances();
        } catch (error) {
            console.error('Failed to save profile:', error);
        } finally {
            setSaving(false);
        }
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long'
        });
    };

    if (loading) {
        return (
            <div className="max-w-4xl mx-auto px-4 py-8">
                <div className="flex items-start gap-6 mb-8">
                    <Skeleton className="w-24 h-24 rounded-sm bg-white/5" />
                    <div className="flex-1">
                        <Skeleton className="h-8 w-48 mb-2 bg-white/5" />
                        <Skeleton className="h-4 w-32 bg-white/5" />
                    </div>
                </div>
                <div className="grid grid-cols-4 gap-4 mb-8">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <Skeleton key={i} className="h-24 bg-white/5 rounded-sm" />
                    ))}
                </div>
            </div>
        );
    }

    if (!profile) {
        return (
            <div className="max-w-4xl mx-auto px-4 py-8 text-center">
                <h1 className="font-display font-bold text-2xl mb-4">Artist Not Found</h1>
                <Link to="/feed" className="text-primary hover:underline">
                    Back to Feed
                </Link>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto px-4 py-8">
            {/* Profile Header */}
            <div className="candy-card rounded-2xl p-6 mb-8" data-testid="profile-header">
                <div className="flex flex-col sm:flex-row items-start gap-6">
                    {/* Avatar */}
                    <Avatar className="w-24 h-24 border-2 border-white/20">
                        <AvatarImage src={profile.picture} />
                        <AvatarFallback className="bg-paper text-2xl font-display font-bold">
                            {profile.username?.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                    </Avatar>

                    {/* Info */}
                    <div className="flex-1 w-full">
                        <div className="flex items-start justify-between mb-3">
                            <div className="flex-1 min-w-0">
                                {editing ? (
                                    <input
                                        value={draft.artist_name}
                                        onChange={e => setDraft(d => ({ ...d, artist_name: e.target.value }))}
                                        maxLength={40}
                                        placeholder="Artist name"
                                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xl font-display font-bold text-white placeholder-zinc-600 focus:outline-none focus:border-neon-blue/50"
                                        autoFocus
                                    />
                                ) : (
                                    <h1 className="font-display font-bold text-2xl sm:text-3xl mb-1 flex items-center gap-2">
                                        {profile.artist_name || profile.username}
                                        {isOwnProfile && (
                                            <button onClick={startEdit} className="text-zinc-500 hover:text-white transition-colors" title="Edit profile">
                                                <Pencil className="w-4 h-4" />
                                            </button>
                                        )}
                                    </h1>
                                )}
                                <p className="text-zinc-400 mt-1">@{profile.username}</p>
                            </div>
                            <span className={`text-[9px] px-2 py-0.5 rounded-full font-mono font-bold uppercase ${getTierColor(profile.tier)}`}>
                                {profile.tier}
                            </span>
                        </div>

                        {editing ? (
                            <input
                                value={draft.location}
                                onChange={e => setDraft(d => ({ ...d, location: e.target.value }))}
                                maxLength={60}
                                placeholder="Location — e.g. Atlanta, GA"
                                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-neon-blue/50 mb-3"
                            />
                        ) : (
                            <div className="flex items-center flex-wrap gap-4 text-sm text-zinc-500 mb-3">
                                {profile.location && (
                                    <span className="flex items-center gap-1">
                                        <MapPin className="w-4 h-4" />
                                        {profile.location}
                                    </span>
                                )}
                                <span className="flex items-center gap-1">
                                    <Calendar className="w-4 h-4" />
                                    Joined {formatDate(profile.created_at)}
                                </span>
                            </div>
                        )}

                        {/* Bio */}
                        {editing ? (
                            <div>
                                <textarea
                                    value={draft.bio}
                                    onChange={e => setDraft(d => ({ ...d, bio: e.target.value }))}
                                    maxLength={280}
                                    rows={3}
                                    placeholder="Tell the arena who you are..."
                                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-600 resize-none focus:outline-none focus:border-neon-blue/50 font-grotesk"
                                />
                                <div className="flex items-center justify-between mt-1.5">
                                    <span className="text-[10px] font-mono text-zinc-600">{draft.bio.length}/280</span>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={cancelEdit}
                                            className="flex items-center gap-1 text-xs text-zinc-500 hover:text-white transition-colors px-2 py-1"
                                        >
                                            <X className="w-3 h-3" /> Cancel
                                        </button>
                                        <button
                                            onClick={saveProfile}
                                            disabled={saving}
                                            className="flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-md transition-all"
                                            style={{ background: '#39FF14', color: '#04120a', opacity: saving ? 0.6 : 1 }}
                                        >
                                            <Check className="w-3 h-3" /> Save
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            profile.bio ? (
                                <p className="text-sm leading-relaxed text-zinc-300">{profile.bio}</p>
                            ) : (
                                isOwnProfile && <p className="text-sm text-zinc-600 italic">Add your name, location &amp; bio — tap the pencil above.</p>
                            )
                        )}
                    </div>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-testid="profile-stats">
                <div className="candy-card rounded-xl p-4 text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                        <Trophy className="w-5 h-5 text-truth" />
                    </div>
                    <p className={`font-mono text-2xl font-bold ${profile.tier === 'legend' ? 'text-truth' : 'text-white'}`}>
                        {profile.rank_score.toFixed(0)}
                    </p>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Rank Score</p>
                </div>

                <div className="candy-card rounded-xl p-4 text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                        <Star className="w-5 h-5 text-gold" />
                    </div>
                    <p className={`font-mono text-2xl font-bold ${getScoreColor(profile.avg_rating)}`}>
                        {profile.avg_rating.toFixed(1)}
                    </p>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Avg Rating</p>
                </div>

                <div className="candy-card rounded-xl p-4 text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                        <Mic2 className="w-5 h-5 text-neon-blue" />
                    </div>
                    <p className="font-mono text-2xl font-bold text-white">
                        {profile.total_performances}
                    </p>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Performances</p>
                </div>

                <div className="candy-card rounded-xl p-4 text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                        <MessageSquare className="w-5 h-5" style={{ color: '#8B5CFF' }} />
                    </div>
                    <p className="font-mono text-2xl font-bold text-white">
                        {profile.total_judgments_received}
                    </p>
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Judgments</p>
                </div>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="performances" className="w-full">
                <TabsList className="bg-paper border border-white/10 rounded-sm p-1 mb-6">
                    <TabsTrigger 
                        value="performances" 
                        className="data-[state=active]:bg-white/10 rounded-sm font-mono text-sm"
                    >
                        Performances ({performances.length})
                    </TabsTrigger>
                    <TabsTrigger 
                        value="stats" 
                        className="data-[state=active]:bg-white/10 rounded-sm font-mono text-sm"
                    >
                        Stats
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="performances" data-testid="performances-tab">
                    {performances.length > 0 ? (
                        <div className="space-y-4">
                            {performances.map((perf) => (
                                <Link
                                    key={perf.performance_id}
                                    to={`/performance/${perf.performance_id}`}
                                    className="candy-card rounded-xl p-4 flex items-center gap-4 hover:scale-[1.005] transition-all duration-300 group"
                                    data-testid={`profile-perf-${perf.performance_id}`}
                                >
                                    {/* Play Button */}
                                    <div className="w-12 h-12 rounded-sm bg-white/5 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                                        <Play className="w-5 h-5 text-zinc-400 group-hover:text-primary" />
                                    </div>

                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-medium truncate group-hover:text-primary transition-colors">
                                            {perf.title}
                                        </h3>
                                        <div className="flex items-center gap-3 text-xs text-zinc-500">
                                            {perf.performance_type === 'freestyle' && (
                                                <Badge variant="outline" className="text-[10px] uppercase">
                                                    Freestyle
                                                </Badge>
                                            )}
                                            <span>{new Date(perf.created_at).toLocaleDateString()}</span>
                                        </div>
                                    </div>

                                    {/* Score */}
                                    {perf.judgment_count > 0 ? (
                                        <div className="text-right">
                                            <p className={`font-mono text-xl font-bold ${getScoreColor(perf.overall_score)}`}>
                                                {perf.overall_score.toFixed(1)}
                                            </p>
                                            <p className="text-xs text-zinc-500">{perf.judgment_count} ratings</p>
                                        </div>
                                    ) : (
                                        <span className="text-xs text-zinc-500">No ratings</span>
                                    )}
                                </Link>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-16 candy-card rounded-2xl">
                            <Mic2 className="w-12 h-12 mx-auto text-zinc-600 mb-4" />
                            <h3 className="font-display font-bold text-lg mb-2">No performances yet</h3>
                            <p className="text-zinc-400 text-sm">
                                {isOwnProfile 
                                    ? 'Upload your first performance to get started'
                                    : 'This artist hasn\'t uploaded any performances yet'
                                }
                            </p>
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="stats" data-testid="stats-tab">
                    <div className="candy-card rounded-2xl p-6">
                        <h3 className="font-display font-bold mb-6">Career Statistics</h3>
                        
                        <div className="space-y-4">
                            {/* Tier Progress */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm text-zinc-400">Tier Progress</span>
                                    <span className={`text-[9px] px-2 py-0.5 rounded-full font-mono font-bold uppercase ${getTierColor(profile.tier)}`}>
                                        {profile.tier}
                                    </span>
                                </div>
                                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                    <div 
                                        className="h-full bg-gradient-to-r from-neon-blue to-truth transition-all duration-500"
                                        style={{ 
                                            width: `${Math.min((profile.rank_score / 2000) * 100, 100)}%` 
                                        }}
                                    />
                                </div>
                                <div className="flex justify-between mt-1 text-xs text-zinc-500 font-mono">
                                    <span>0</span>
                                    <span>2000 (Legend)</span>
                                </div>
                            </div>

                            {/* Stats Grid */}
                            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/10">
                                <div>
                                    <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Judgments Given</p>
                                    <p className="font-mono text-lg font-bold">{profile.total_judgments_given}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Judgments Received</p>
                                    <p className="font-mono text-lg font-bold">{profile.total_judgments_received}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Total Performances</p>
                                    <p className="font-mono text-lg font-bold">{profile.total_performances}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Average Rating</p>
                                    <p className={`font-mono text-lg font-bold ${getScoreColor(profile.avg_rating)}`}>
                                        {profile.avg_rating.toFixed(2)}/10
                                    </p>
                                </div>
                            </div>

                            {/* Tier Breakdown */}
                            <div className="pt-4 border-t border-white/10">
                                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Tier Requirements</p>
                                <div className="space-y-2">
                                    {[
                                        { name: 'Rookie', min: 0, max: 100 },
                                        { name: 'Contender', min: 100, max: 300 },
                                        { name: 'Prospect', min: 300, max: 600 },
                                        { name: 'Pro', min: 600, max: 1000 },
                                        { name: 'Elite', min: 1000, max: 2000 },
                                        { name: 'Legend', min: 2000, max: Infinity },
                                    ].map((tier) => {
                                        const isCurrentTier = profile.tier === tier.name.toLowerCase();
                                        const isPastTier = profile.rank_score >= tier.max;
                                        return (
                                            <div 
                                                key={tier.name}
                                                className={`flex items-center justify-between py-2 px-3 rounded-sm ${
                                                    isCurrentTier ? 'bg-primary/10 border border-primary/30' : ''
                                                }`}
                                            >
                                                <span className={`text-sm ${
                                                    isPastTier || isCurrentTier ? 'text-white' : 'text-zinc-500'
                                                }`}>
                                                    {tier.name}
                                                </span>
                                                <span className="font-mono text-xs text-zinc-500">
                                                    {tier.min}+ pts
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default ProfilePage;
